// Figures registry for the 3D geometry prototype.
// Each figure declares: params (dim controls), build(dims) returning FigureData.
// "Lines" are the selectable special lines. cutPoly returns an ordered array of
// points on the cut plane — for curved solids it samples.

import * as THREE from 'three';

// ---------- Type contract ----------

export interface CutPlane {
  normal: THREE.Vector3;
  mid: THREE.Vector3;
  lineDir: THREE.Vector3;
  p1: THREE.Vector3;
  p2: THREE.Vector3;
}

export interface Vertex extends THREE.Vector3 {
  id?: string;
  name?: string;
}

export type SpecialLine =
  | { v1: number | null; v2: number | null; kind?: string; name?: string; info?: string }
  | { a: THREE.Vector3; b: THREE.Vector3; kind?: string; name?: string; info?: string };

export interface FigureParam {
  id: string;
  labelKey: string;
  min: number;
  max: number;
  step: number;
  value: number;
}

export interface FigureData {
  vertices: Vertex[];
  edges: [number, number][];
  bodyGeometry: THREE.BufferGeometry;
  specialLines: { face: SpecialLine[]; space: SpecialLine[] };
  cutPoly: (plane: CutPlane) => THREE.Vector3[] | null;
}

export interface FigureDef {
  labelKey: string;
  params: readonly FigureParam[];
  build(dims: Record<string, number>): FigureData;
}

// ---------- Utilities ----------

function V(x: number, y: number, z: number, id: string, name: string): Vertex {
  const v = new THREE.Vector3(x, y, z) as Vertex;
  v.id = id;
  v.name = name;
  return v;
}

function line(v1: number, v2: number, kind: string, info: string): SpecialLine {
  return { v1, v2, kind, info };
}

// Generic polygon intersection of a plane with a convex polyhedron edges
function cutPolyFromEdges(
  edges: [number, number][],
  vertices: THREE.Vector3[],
  plane: CutPlane,
): THREE.Vector3[] | null {
  const sdist = (v: THREE.Vector3) => v.clone().sub(plane.mid).dot(plane.normal);
  const pts: THREE.Vector3[] = [];
  for (const [i, j] of edges) {
    const a = vertices[i];
    const b = vertices[j];
    if (a === undefined || b === undefined) continue;
    const da = sdist(a);
    const db = sdist(b);
    if (Math.abs(da) < 1e-5) pts.push(a.clone());
    else if (Math.abs(db) < 1e-5) pts.push(b.clone());
    else if (da * db < 0) {
      const t = da / (da - db);
      pts.push(a.clone().lerp(b, t));
    }
  }
  const uniq: THREE.Vector3[] = [];
  for (const p of pts) if (!uniq.some((q) => q.distanceTo(p) < 1e-4)) uniq.push(p);
  if (uniq.length < 3) return null;
  const centroid = new THREE.Vector3();
  uniq.forEach((p) => centroid.add(p));
  centroid.multiplyScalar(1 / uniq.length);
  const u = new THREE.Vector3(1, 0, 0);
  if (Math.abs(u.dot(plane.normal)) > 0.9) u.set(0, 1, 0);
  u.sub(plane.normal.clone().multiplyScalar(u.dot(plane.normal))).normalize();
  const v2 = new THREE.Vector3().crossVectors(plane.normal, u);
  uniq.sort((p, q) => {
    const ap = Math.atan2(p.clone().sub(centroid).dot(v2), p.clone().sub(centroid).dot(u));
    const aq = Math.atan2(q.clone().sub(centroid).dot(v2), q.clone().sub(centroid).dot(u));
    return ap - aq;
  });
  return uniq;
}

// ---------- Helper: extruded polygon geometry ----------

function makeExtrude(poly2D: [number, number][], h: number): THREE.BufferGeometry {
  const shape = new THREE.Shape();
  const [x0, y0] = poly2D[0] ?? [0, 0];
  shape.moveTo(x0, y0);
  for (let k = 1; k < poly2D.length; k++) {
    const [xk, yk] = poly2D[k] ?? [0, 0];
    shape.lineTo(xk, yk);
  }
  shape.closePath();
  const geo = new THREE.ExtrudeGeometry(shape, { depth: h, bevelEnabled: false });
  // Center extrusion along its depth axis (Z), then rotate so the shape's
  // (x,y) plane lands on the world (x,z) plane and shape's depth (Z)
  // becomes world height (Y). We use rotateX(-π/2): (x,y,z) → (x,z,-y).
  // After rotation, shape pt (cos a, sin a, 0) → world (cos a, 0, -sin a),
  // so we also flip Z to match the convention V(cos a · r, yy, sin a · r).
  geo.translate(0, 0, -h / 2);
  geo.rotateX(-Math.PI / 2);
  geo.scale(1, 1, -1);
  geo.computeVertexNormals();
  return geo;
}

// ---------- CUB ----------

function cube(dims: Record<string, number>): FigureData {
  const s = dims['s']!;
  const h = s / 2;
  const verts = [
    V(-h, -h, -h, '000', 'A'),
    V(h, -h, -h, '100', 'B'),
    V(h, h, -h, '110', 'C'),
    V(-h, h, -h, '010', 'D'),
    V(-h, -h, h, '001', 'E'),
    V(h, -h, h, '101', 'F'),
    V(h, h, h, '111', 'G'),
    V(-h, h, h, '011', 'H'),
  ];
  const edges: [number, number][] = [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 0],
    [4, 5],
    [5, 6],
    [6, 7],
    [7, 4],
    [0, 4],
    [1, 5],
    [2, 6],
    [3, 7],
  ];
  // Generate face diagonals from 6 faces × 2 per face
  const faces: [number, number, number, number][] = [
    [0, 1, 2, 3],
    [4, 5, 6, 7], // z- z+
    [0, 1, 5, 4],
    [3, 2, 6, 7], // y- y+
    [0, 3, 7, 4],
    [1, 2, 6, 5], // x- x+
  ];
  const fd: SpecialLine[] = [];
  faces.forEach((f) => {
    fd.push(line(f[0], f[2], 'face', ''));
    fd.push(line(f[1], f[3], 'face', ''));
  });
  const sd: SpecialLine[] = [
    line(0, 6, 'space', ''),
    line(1, 7, 'space', ''),
    line(2, 4, 'space', ''),
    line(3, 5, 'space', ''),
  ];
  return {
    vertices: verts,
    edges,
    bodyGeometry: new THREE.BoxGeometry(s, s, s),
    specialLines: { face: fd, space: sd },
    cutPoly: (plane) => cutPolyFromEdges(edges, verts, plane),
  };
}

// ---------- PARALELIPIPED DREPTUNGHIC ----------

function cuboid(dims: Record<string, number>): FigureData {
  const a = dims['a']!;
  const b = dims['b']!;
  const c = dims['c']!;
  const verts = [
    V(-a / 2, -b / 2, -c / 2, '000', 'A'),
    V(a / 2, -b / 2, -c / 2, '100', 'B'),
    V(a / 2, b / 2, -c / 2, '110', 'C'),
    V(-a / 2, b / 2, -c / 2, '010', 'D'),
    V(-a / 2, -b / 2, c / 2, '001', 'E'),
    V(a / 2, -b / 2, c / 2, '101', 'F'),
    V(a / 2, b / 2, c / 2, '111', 'G'),
    V(-a / 2, b / 2, c / 2, '011', 'H'),
  ];
  const edges: [number, number][] = [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 0],
    [4, 5],
    [5, 6],
    [6, 7],
    [7, 4],
    [0, 4],
    [1, 5],
    [2, 6],
    [3, 7],
  ];
  const faces: [number, number, number, number][] = [
    [0, 1, 2, 3],
    [4, 5, 6, 7],
    [0, 1, 5, 4],
    [3, 2, 6, 7],
    [0, 3, 7, 4],
    [1, 2, 6, 5],
  ];
  const fd: SpecialLine[] = [];
  faces.forEach((f) => {
    fd.push(line(f[0], f[2], 'face', ''));
    fd.push(line(f[1], f[3], 'face', ''));
  });
  const sd: SpecialLine[] = [
    line(0, 6, 'space', ''),
    line(1, 7, 'space', ''),
    line(2, 4, 'space', ''),
    line(3, 5, 'space', ''),
  ];
  return {
    vertices: verts,
    edges,
    bodyGeometry: new THREE.BoxGeometry(a, b, c),
    specialLines: { face: fd, space: sd },
    cutPoly: (plane) => cutPolyFromEdges(edges, verts, plane),
  };
}

// ---------- PRISMĂ TRIUNGHIULARĂ REGULATĂ ----------

function triPrism(dims: Record<string, number>): FigureData {
  const s = dims['s']!;
  const h = dims['h']!;
  const r = s / Math.sqrt(3); // circumscribed radius
  const mkT = (yy: number, tag: string, nameOffset: number): Vertex[] =>
    [0, 1, 2].map((k) => {
      const ang = Math.PI / 2 + (k * 2 * Math.PI) / 3;
      return V(
        Math.cos(ang) * r,
        yy,
        Math.sin(ang) * r,
        `${tag}${k}`,
        ['A', 'B', 'C', 'D', 'E', 'F'][k + nameOffset] ?? String.fromCharCode(65 + k + nameOffset),
      );
    });
  const bot = mkT(-h / 2, 'b', 0);
  const top = mkT(h / 2, 't', 3);
  const verts = [...bot, ...top];
  const edges: [number, number][] = [
    [0, 1],
    [1, 2],
    [2, 0],
    [3, 4],
    [4, 5],
    [5, 3],
    [0, 3],
    [1, 4],
    [2, 5],
  ];
  // "Face diagonals" on the 3 rectangular lateral faces: each face has 2 diagonals
  const faceDiag: SpecialLine[] = [];
  for (let k = 0; k < 3; k++) {
    const k2 = (k + 1) % 3;
    faceDiag.push(line(k, k2 + 3, 'face', ''));
    faceDiag.push(line(k + 3, k2, 'face', ''));
  }
  // "Space diagonals" for a triangular prism: segments from a bottom vertex to
  // non-adjacent top vertex — same as face diagonals. Use 0 for this fig.
  return {
    vertices: verts,
    edges,
    bodyGeometry: makeExtrude(
      [
        [r * Math.cos(Math.PI / 2), r * Math.sin(Math.PI / 2)],
        [
          r * Math.cos(Math.PI / 2 + (2 * Math.PI) / 3),
          r * Math.sin(Math.PI / 2 + (2 * Math.PI) / 3),
        ],
        [
          r * Math.cos(Math.PI / 2 + (4 * Math.PI) / 3),
          r * Math.sin(Math.PI / 2 + (4 * Math.PI) / 3),
        ],
      ],
      h,
    ),
    specialLines: { face: faceDiag, space: [] },
    cutPoly: (plane) => cutPolyFromEdges(edges, verts, plane),
  };
}

// ---------- PRISMĂ HEXAGONALĂ REGULATĂ ----------

function hexPrism(dims: Record<string, number>): FigureData {
  const s = dims['s']!;
  const h = dims['h']!;
  const r = s;
  const mkH = (yy: number, tag: string, baseIdx: number): Vertex[] =>
    [0, 1, 2, 3, 4, 5].map((k) => {
      const ang = (k * Math.PI) / 3;
      return V(
        Math.cos(ang) * r,
        yy,
        Math.sin(ang) * r,
        `${tag}${k}`,
        String.fromCharCode(65 + baseIdx + k),
      );
    });
  const bot = mkH(-h / 2, 'b', 0);
  const top = mkH(h / 2, 't', 6);
  const verts = [...bot, ...top];
  const edges: [number, number][] = [];
  for (let k = 0; k < 6; k++) {
    edges.push([k, (k + 1) % 6]);
    edges.push([k + 6, ((k + 1) % 6) + 6]);
    edges.push([k, k + 6]);
  }
  // Face diagonals on 6 rectangular faces
  const fd: SpecialLine[] = [];
  for (let k = 0; k < 6; k++) {
    const k2 = (k + 1) % 6;
    fd.push(line(k, k2 + 6, 'face', ''));
    fd.push(line(k + 6, k2, 'face', ''));
  }
  // Space diagonals: from bottom vertex to diametrically opposite top vertex
  const sd: SpecialLine[] = [];
  for (let k = 0; k < 6; k++) sd.push(line(k, ((k + 3) % 6) + 6, 'space', ''));
  const poly: [number, number][] = [];
  for (let k = 0; k < 6; k++) {
    const ang = (k * Math.PI) / 3;
    poly.push([r * Math.cos(ang), r * Math.sin(ang)]);
  }
  return {
    vertices: verts,
    edges,
    bodyGeometry: makeExtrude(poly, h),
    specialLines: { face: fd, space: sd },
    cutPoly: (plane) => cutPolyFromEdges(edges, verts, plane),
  };
}

// ---------- TETRAEDRU REGULAT ----------

function tetra(dims: Record<string, number>): FigureData {
  const s = dims['s']!;
  const r = (s * Math.sqrt(3)) / 3; // circumradius of base triangle
  const hTri = s * Math.sqrt(2 / 3); // height of regular tetrahedron
  const y0 = -hTri / 2;
  const y1 = hTri / 2;
  const bot = [0, 1, 2].map((k) => {
    const ang = Math.PI / 2 + (k * 2 * Math.PI) / 3;
    return V(Math.cos(ang) * r, y0, Math.sin(ang) * r, `b${k}`, ['A', 'B', 'C'][k] ?? '?');
  });
  const apex = V(0, y1, 0, 'ap', 'D');
  const verts = [...bot, apex];
  const edges: [number, number][] = [
    [0, 1],
    [1, 2],
    [2, 0],
    [0, 3],
    [1, 3],
    [2, 3],
  ];
  // For a tetrahedron, expose the 4 altitudes from each vertex to the centroid
  // of the opposite face as 'space' special lines.
  const altLines: SpecialLine[] = [];
  // Centroid of bottom face (index 0, 1, 2):
  const v0 = verts[0]!;
  const v1 = verts[1]!;
  const v2 = verts[2]!;
  const v3 = verts[3]!;
  const cBot = new THREE.Vector3()
    .add(v0)
    .add(v1)
    .add(v2)
    .multiplyScalar(1 / 3);
  altLines.push({
    kind: 'space',
    a: v3.clone(),
    b: cBot,
    name: 'D→mABC',
    info: 'info.height',
  });
  // From each base vertex to centroid of opposite face
  for (let k = 0; k < 3; k++) {
    const k1 = (k + 1) % 3;
    const k2 = (k + 2) % 3;
    const vk = verts[k]!;
    const vk1 = verts[k1]!;
    const vk2 = verts[k2]!;
    const c = new THREE.Vector3()
      .add(vk1)
      .add(vk2)
      .add(v3)
      .multiplyScalar(1 / 3);
    altLines.push({
      kind: 'space',
      a: vk.clone(),
      b: c,
      name: `${['A', 'B', 'C'][k] ?? '?'}→opposite`,
      info: 'info.median',
    });
  }
  // Geometry: make a custom BufferGeometry from 4 triangular faces
  const geo = new THREE.BufferGeometry();
  const pos: number[] = [];
  function addTri(i: number, j: number, k: number): void {
    const p = verts[i]!;
    const q = verts[j]!;
    const r2 = verts[k]!;
    pos.push(p.x, p.y, p.z, q.x, q.y, q.z, r2.x, r2.y, r2.z);
  }
  addTri(0, 2, 1);
  addTri(0, 1, 3);
  addTri(1, 2, 3);
  addTri(2, 0, 3);
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.computeVertexNormals();
  return {
    vertices: verts,
    edges,
    bodyGeometry: geo,
    specialLines: { face: [], space: altLines },
    cutPoly: (plane) => cutPolyFromEdges(edges, verts, plane),
  };
}

// ---------- PIRAMIDĂ PATRULATERĂ REGULATĂ ----------

function pyramid4(dims: Record<string, number>): FigureData {
  const s = dims['s']!;
  const h = dims['h']!;
  const hh = s / 2;
  const bot = [
    V(-hh, -h / 2, -hh, 'b0', 'A'),
    V(hh, -h / 2, -hh, 'b1', 'B'),
    V(hh, -h / 2, hh, 'b2', 'C'),
    V(-hh, -h / 2, hh, 'b3', 'D'),
  ];
  const apex = V(0, h / 2, 0, 'ap', 'E');
  const verts = [...bot, apex];
  const edges: [number, number][] = [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 0],
    [0, 4],
    [1, 4],
    [2, 4],
    [3, 4],
  ];
  // Face diagonals on the square base: AC, BD
  const fd: SpecialLine[] = [line(0, 2, 'face', ''), line(1, 3, 'face', '')];
  // apex to base-edge midpoints (apoteme)
  const sd: SpecialLine[] = [];
  for (let k = 0; k < 4; k++) {
    const vk = verts[k]!;
    const vnext = verts[(k + 1) % 4]!;
    const mid = vk.clone().lerp(vnext, 0.5);
    sd.push({
      kind: 'space',
      a: verts[4]!.clone(),
      b: mid,
      name: `E→m${String.fromCharCode(65 + k)}${String.fromCharCode(65 + ((k + 1) % 4))}`,
      info: 'info.apothem',
    });
  }
  // apex to base center (height)
  const cen = new THREE.Vector3()
    .add(verts[0]!)
    .add(verts[1]!)
    .add(verts[2]!)
    .add(verts[3]!)
    .multiplyScalar(1 / 4);
  sd.push({
    kind: 'space',
    a: verts[4]!.clone(),
    b: cen,
    name: 'E→O',
    info: 'info.height',
  });
  const geo = new THREE.BufferGeometry();
  const pos: number[] = [];
  function addTri(i: number, j: number, k: number): void {
    const p = verts[i]!;
    const q = verts[j]!;
    const r2 = verts[k]!;
    pos.push(p.x, p.y, p.z, q.x, q.y, q.z, r2.x, r2.y, r2.z);
  }
  // base (2 triangles)
  addTri(0, 2, 1);
  addTri(0, 3, 2);
  // lateral faces
  addTri(0, 1, 4);
  addTri(1, 2, 4);
  addTri(2, 3, 4);
  addTri(3, 0, 4);
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.computeVertexNormals();
  return {
    vertices: verts,
    edges,
    bodyGeometry: geo,
    specialLines: { face: fd, space: sd },
    cutPoly: (plane) => cutPolyFromEdges(edges, verts, plane),
  };
}

// ---------- CILINDRU ----------

function cutPolyCylinder(r: number, h: number, plane: CutPlane): THREE.Vector3[] | null {
  // Sample top and bottom circle edges, plus check for plane crossing each generator.
  // Simpler: triangulate the cylinder surface and use cutPolyFromEdges on sampled edges.
  const N = 96;
  const verts: THREE.Vector3[] = [];
  for (let k = 0; k < N; k++) {
    const ang = (k * 2 * Math.PI) / N;
    verts.push(new THREE.Vector3(r * Math.cos(ang), -h / 2, r * Math.sin(ang)));
  }
  for (let k = 0; k < N; k++) {
    const ang = (k * 2 * Math.PI) / N;
    verts.push(new THREE.Vector3(r * Math.cos(ang), h / 2, r * Math.sin(ang)));
  }
  const edges: [number, number][] = [];
  for (let k = 0; k < N; k++) {
    edges.push([k, (k + 1) % N]);
    edges.push([N + k, N + ((k + 1) % N)]);
    edges.push([k, N + k]);
  }
  return cutPolyFromEdges(edges, verts, plane);
}

function cylinder(dims: Record<string, number>): FigureData {
  const r = dims['r']!;
  const h = dims['h']!;
  // Expose 2 "vertices" at centers of top/bottom and parametric lines; since
  // cylinder has no real vertices, we mark O1, O2 and two generators A-B and
  // C-D on opposite sides.
  const y0 = -h / 2;
  const y1 = h / 2;
  const verts = [
    V(0, y0, 0, 'O1', 'O₁'),
    V(0, y1, 0, 'O2', 'O₂'),
    V(r, y0, 0, 'A', 'A'),
    V(r, y1, 0, 'B', 'B'),
    V(-r, y0, 0, 'C', 'C'),
    V(-r, y1, 0, 'D', 'D'),
  ];
  const sd: SpecialLine[] = [
    { kind: 'space', a: verts[0]!.clone(), b: verts[1]!.clone(), name: 'O₁O₂', info: 'info.axis' },
    {
      kind: 'space',
      a: verts[2]!.clone(),
      b: verts[4]!.clone(),
      name: 'AC',
      info: 'info.diameterBase',
    },
    {
      kind: 'space',
      a: verts[3]!.clone(),
      b: verts[5]!.clone(),
      name: 'BD',
      info: 'info.diameterTop',
    },
  ];
  const fd: SpecialLine[] = [
    {
      kind: 'face',
      a: verts[2]!.clone(),
      b: verts[3]!.clone(),
      name: 'AB',
      info: 'info.generator',
    },
    {
      kind: 'face',
      a: verts[4]!.clone(),
      b: verts[5]!.clone(),
      name: 'CD',
      info: 'info.generator',
    },
    // axial diagonal
    { kind: 'face', a: verts[2]!.clone(), b: verts[5]!.clone(), name: 'AD', info: 'info.axial' },
    { kind: 'face', a: verts[4]!.clone(), b: verts[3]!.clone(), name: 'CB', info: 'info.axial' },
  ];
  return {
    vertices: verts,
    edges: [],
    bodyGeometry: new THREE.CylinderGeometry(r, r, h, 48),
    specialLines: { face: fd, space: sd },
    cutPoly: (plane) => cutPolyCylinder(r, h, plane),
  };
}

// ---------- CON ----------

function cutPolyCone(r: number, h: number, plane: CutPlane): THREE.Vector3[] | null {
  const N = 96;
  const apex = new THREE.Vector3(0, h / 2, 0);
  const verts: THREE.Vector3[] = [apex];
  for (let k = 0; k < N; k++) {
    const ang = (k * 2 * Math.PI) / N;
    verts.push(new THREE.Vector3(r * Math.cos(ang), -h / 2, r * Math.sin(ang)));
  }
  const edges: [number, number][] = [];
  for (let k = 0; k < N; k++) {
    edges.push([1 + k, 1 + ((k + 1) % N)]);
    edges.push([0, 1 + k]);
  }
  return cutPolyFromEdges(edges, verts, plane);
}

function cone(dims: Record<string, number>): FigureData {
  const r = dims['r']!;
  const h = dims['h']!;
  const verts = [
    V(0, h / 2, 0, 'V', 'V'),
    V(0, -h / 2, 0, 'O', 'O'),
    V(r, -h / 2, 0, 'A', 'A'),
    V(-r, -h / 2, 0, 'B', 'B'),
  ];
  const fd: SpecialLine[] = [
    {
      kind: 'face',
      a: verts[0]!.clone(),
      b: verts[2]!.clone(),
      name: 'VA',
      info: 'info.generator',
    },
    {
      kind: 'face',
      a: verts[0]!.clone(),
      b: verts[3]!.clone(),
      name: 'VB',
      info: 'info.generator',
    },
  ];
  const sd: SpecialLine[] = [
    { kind: 'space', a: verts[0]!.clone(), b: verts[1]!.clone(), name: 'VO', info: 'info.axis' },
    {
      kind: 'space',
      a: verts[2]!.clone(),
      b: verts[3]!.clone(),
      name: 'AB',
      info: 'info.diameter',
    },
  ];
  return {
    vertices: verts,
    edges: [],
    bodyGeometry: new THREE.ConeGeometry(r, h, 48),
    specialLines: { face: fd, space: sd },
    cutPoly: (plane) => cutPolyCone(r, h, plane),
  };
}

// ---------- SFERĂ ----------

function cutPolySphere(r: number, plane: CutPlane): THREE.Vector3[] | null {
  // Intersection of sphere with plane is a circle. Sample it.
  // distance from origin to plane
  const distOrig = plane.normal.dot(plane.mid);
  if (Math.abs(distOrig) >= r) return null;
  const rCirc = Math.sqrt(r * r - distOrig * distOrig);
  const center = plane.normal.clone().multiplyScalar(distOrig);
  const u = new THREE.Vector3(1, 0, 0);
  if (Math.abs(u.dot(plane.normal)) > 0.9) u.set(0, 1, 0);
  u.sub(plane.normal.clone().multiplyScalar(u.dot(plane.normal))).normalize();
  const v2 = new THREE.Vector3().crossVectors(plane.normal, u);
  const N = 96;
  const pts: THREE.Vector3[] = [];
  for (let k = 0; k < N; k++) {
    const ang = (k * 2 * Math.PI) / N;
    pts.push(
      center
        .clone()
        .addScaledVector(u, rCirc * Math.cos(ang))
        .addScaledVector(v2, rCirc * Math.sin(ang)),
    );
  }
  return pts;
}

function sphere(dims: Record<string, number>): FigureData {
  const r = dims['r']!;
  const verts = [
    V(0, r, 0, 'N', 'N'),
    V(0, -r, 0, 'S', 'S'),
    V(r, 0, 0, 'E', 'E'),
    V(-r, 0, 0, 'W', 'W'),
  ];
  const sd: SpecialLine[] = [
    {
      kind: 'space',
      a: verts[0]!.clone(),
      b: verts[1]!.clone(),
      name: 'NS',
      info: 'info.diameter',
    },
    {
      kind: 'space',
      a: verts[2]!.clone(),
      b: verts[3]!.clone(),
      name: 'EW',
      info: 'info.diameter',
    },
  ];
  return {
    vertices: verts,
    edges: [],
    bodyGeometry: new THREE.SphereGeometry(r, 48, 32),
    specialLines: { face: [], space: sd },
    cutPoly: (plane) => cutPolySphere(r, plane),
  };
}

// ---------- TRUNCHI DE PIRAMIDĂ PATRULATERĂ ----------

function frustum4(dims: Record<string, number>): FigureData {
  const s1 = dims['s1']!;
  const s2 = dims['s2']!;
  const h = dims['h']!;
  const h1 = s1 / 2;
  const h2 = s2 / 2;
  const bot = [
    V(-h1, -h / 2, -h1, 'b0', 'A'),
    V(h1, -h / 2, -h1, 'b1', 'B'),
    V(h1, -h / 2, h1, 'b2', 'C'),
    V(-h1, -h / 2, h1, 'b3', 'D'),
  ];
  const top = [
    V(-h2, h / 2, -h2, 't0', 'E'),
    V(h2, h / 2, -h2, 't1', 'F'),
    V(h2, h / 2, h2, 't2', 'G'),
    V(-h2, h / 2, h2, 't3', 'H'),
  ];
  const verts = [...bot, ...top];
  const edges: [number, number][] = [
    [0, 1],
    [1, 2],
    [2, 3],
    [3, 0],
    [4, 5],
    [5, 6],
    [6, 7],
    [7, 4],
    [0, 4],
    [1, 5],
    [2, 6],
    [3, 7],
  ];
  const fd: SpecialLine[] = [
    line(0, 2, 'face', ''),
    line(1, 3, 'face', ''),
    line(4, 6, 'face', ''),
    line(5, 7, 'face', ''),
  ];
  // diagonals of 4 lateral trapezoidal faces
  for (let k = 0; k < 4; k++) {
    const k2 = (k + 1) % 4;
    fd.push(line(k, k2 + 4, 'face', ''));
    fd.push(line(k + 4, k2, 'face', ''));
  }
  const sd: SpecialLine[] = [
    line(0, 6, 'space', ''),
    line(1, 7, 'space', ''),
    line(2, 4, 'space', ''),
    line(3, 5, 'space', ''),
  ];
  // geometry: custom buffer
  const geo = new THREE.BufferGeometry();
  const pos: number[] = [];
  function addTri(i: number, j: number, k: number): void {
    const p = verts[i]!;
    const q = verts[j]!;
    const r2 = verts[k]!;
    pos.push(p.x, p.y, p.z, q.x, q.y, q.z, r2.x, r2.y, r2.z);
  }
  // bottom
  addTri(0, 2, 1);
  addTri(0, 3, 2);
  // top
  addTri(4, 5, 6);
  addTri(4, 6, 7);
  // sides
  for (let k = 0; k < 4; k++) {
    const k2 = (k + 1) % 4;
    addTri(k, k2, k2 + 4);
    addTri(k, k2 + 4, k + 4);
  }
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.computeVertexNormals();
  return {
    vertices: verts,
    edges,
    bodyGeometry: geo,
    specialLines: { face: fd, space: sd },
    cutPoly: (plane) => cutPolyFromEdges(edges, verts, plane),
  };
}

// ---------- TRUNCHI DE CON ----------

function cutPolyConeFrustum(
  r1: number,
  r2: number,
  h: number,
  plane: CutPlane,
): THREE.Vector3[] | null {
  const N = 96;
  const verts: THREE.Vector3[] = [];
  for (let k = 0; k < N; k++) {
    const ang = (k * 2 * Math.PI) / N;
    verts.push(new THREE.Vector3(r1 * Math.cos(ang), -h / 2, r1 * Math.sin(ang)));
  }
  for (let k = 0; k < N; k++) {
    const ang = (k * 2 * Math.PI) / N;
    verts.push(new THREE.Vector3(r2 * Math.cos(ang), h / 2, r2 * Math.sin(ang)));
  }
  const edges: [number, number][] = [];
  for (let k = 0; k < N; k++) {
    edges.push([k, (k + 1) % N]);
    edges.push([N + k, N + ((k + 1) % N)]);
    edges.push([k, N + k]);
  }
  return cutPolyFromEdges(edges, verts, plane);
}

function coneFrustum(dims: Record<string, number>): FigureData {
  const r1 = dims['r1']!;
  const r2 = dims['r2']!;
  const h = dims['h']!;
  const verts = [
    V(0, -h / 2, 0, 'O1', 'O₁'),
    V(0, h / 2, 0, 'O2', 'O₂'),
    V(r1, -h / 2, 0, 'A', 'A'),
    V(r2, h / 2, 0, 'B', 'B'),
    V(-r1, -h / 2, 0, 'C', 'C'),
    V(-r2, h / 2, 0, 'D', 'D'),
  ];
  const fd: SpecialLine[] = [
    {
      kind: 'face',
      a: verts[2]!.clone(),
      b: verts[3]!.clone(),
      name: 'AB',
      info: 'info.generator',
    },
    {
      kind: 'face',
      a: verts[4]!.clone(),
      b: verts[5]!.clone(),
      name: 'CD',
      info: 'info.generator',
    },
  ];
  const sd: SpecialLine[] = [
    { kind: 'space', a: verts[0]!.clone(), b: verts[1]!.clone(), name: 'O₁O₂', info: 'info.axis' },
    {
      kind: 'space',
      a: verts[2]!.clone(),
      b: verts[4]!.clone(),
      name: 'AC',
      info: 'info.diameterBase',
    },
    {
      kind: 'space',
      a: verts[3]!.clone(),
      b: verts[5]!.clone(),
      name: 'BD',
      info: 'info.diameterTop',
    },
    { kind: 'space', a: verts[2]!.clone(), b: verts[5]!.clone(), name: 'AD', info: 'info.axial' },
  ];
  return {
    vertices: verts,
    edges: [],
    bodyGeometry: new THREE.CylinderGeometry(r2, r1, h, 48),
    specialLines: { face: fd, space: sd },
    cutPoly: (plane) => cutPolyConeFrustum(r1, r2, h, plane),
  };
}

// ---------- Registry ----------

export const FIGURES: Record<string, FigureDef> = {
  cuboid: {
    labelKey: 'fig.cuboid',
    params: [
      { id: 'a', labelKey: 'dim.length', min: 1, max: 5, step: 0.1, value: 3 },
      { id: 'b', labelKey: 'dim.width', min: 1, max: 5, step: 0.1, value: 2 },
      { id: 'c', labelKey: 'dim.height', min: 1, max: 5, step: 0.1, value: 2.5 },
    ],
    build: cuboid,
  },
  cube: {
    labelKey: 'fig.cube',
    params: [{ id: 's', labelKey: 'dim.side', min: 1, max: 5, step: 0.1, value: 2.5 }],
    build: cube,
  },
  triPrism: {
    labelKey: 'fig.triPrism',
    params: [
      { id: 's', labelKey: 'dim.side', min: 1, max: 5, step: 0.1, value: 2.5 },
      { id: 'h', labelKey: 'dim.height', min: 1, max: 5, step: 0.1, value: 3 },
    ],
    build: triPrism,
  },
  hexPrism: {
    labelKey: 'fig.hexPrism',
    params: [
      { id: 's', labelKey: 'dim.side', min: 0.6, max: 3, step: 0.1, value: 1.4 },
      { id: 'h', labelKey: 'dim.height', min: 1, max: 5, step: 0.1, value: 3 },
    ],
    build: hexPrism,
  },
  tetra: {
    labelKey: 'fig.tetrahedron',
    params: [{ id: 's', labelKey: 'dim.side', min: 1, max: 5, step: 0.1, value: 3 }],
    build: tetra,
  },
  pyramid4: {
    labelKey: 'fig.squarePyramid',
    params: [
      { id: 's', labelKey: 'dim.sideBase', min: 1, max: 5, step: 0.1, value: 2.5 },
      { id: 'h', labelKey: 'dim.height', min: 1, max: 5, step: 0.1, value: 3 },
    ],
    build: pyramid4,
  },
  cylinder: {
    labelKey: 'fig.cylinder',
    params: [
      { id: 'r', labelKey: 'dim.radius', min: 0.5, max: 3, step: 0.1, value: 1.3 },
      { id: 'h', labelKey: 'dim.height', min: 1, max: 5, step: 0.1, value: 3 },
    ],
    build: cylinder,
  },
  cone: {
    labelKey: 'fig.cone',
    params: [
      { id: 'r', labelKey: 'dim.radius', min: 0.5, max: 3, step: 0.1, value: 1.5 },
      { id: 'h', labelKey: 'dim.height', min: 1, max: 5, step: 0.1, value: 3 },
    ],
    build: cone,
  },
  sphere: {
    labelKey: 'fig.sphere',
    params: [{ id: 'r', labelKey: 'dim.radius', min: 1, max: 3, step: 0.1, value: 1.8 }],
    build: sphere,
  },
  frustum4: {
    labelKey: 'fig.frustumPyramid',
    params: [
      { id: 's1', labelKey: 'dim.sideBase', min: 1.5, max: 5, step: 0.1, value: 3 },
      { id: 's2', labelKey: 'dim.sideTop', min: 0.5, max: 3, step: 0.1, value: 1.5 },
      { id: 'h', labelKey: 'dim.height', min: 1, max: 5, step: 0.1, value: 2.5 },
    ],
    build: frustum4,
  },
  coneFrustum: {
    labelKey: 'fig.frustumCone',
    params: [
      { id: 'r1', labelKey: 'dim.r1', min: 0.8, max: 3, step: 0.1, value: 1.7 },
      { id: 'r2', labelKey: 'dim.r2', min: 0.3, max: 2, step: 0.1, value: 0.9 },
      { id: 'h', labelKey: 'dim.height', min: 1, max: 5, step: 0.1, value: 2.5 },
    ],
    build: coneFrustum,
  },
};
